export interface AlertButton {
  text?: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
}

export interface AlertOptions {
  cancelable?: boolean;
  onDismiss?: () => void;
}

/**
 * Show a toast message on web using browser Alert
 * Note: Web doesn't have native toast, so we use a simple alert
 */
export const showToast = (message: string, duration: 'short' | 'long' = 'short') => {
  // On web, use a simple non-blocking alert
  // In production, you might want to use a toast library like react-toastify
  console.log(`[Toast]: ${message}`);
  
  // For now, just use console.log - you can enhance this with a proper toast library later
  // Or show a temporary styled div notification
};

export const showSuccessToast = (message: string) => {
  showToast(message, 'short');
};

export const showErrorToast = (message: string) => {
  showToast(message, 'long');
};

/**
 * Smart Alert wrapper for web using browser dialogs
 */
export const Alert = {
  alert: (
    title: string,
    message?: string,
    buttons?: AlertButton[],
    options?: AlertOptions
  ) => {
    // If it's a success message, show as toast
    if (title === 'Success' || title.toLowerCase() === 'success') {
      showSuccessToast(message || title);
      
      // If there are buttons with onPress handlers, execute the first one
      if (buttons && buttons.length > 0) {
        const primaryButton = buttons.find(btn => btn.style !== 'cancel') || buttons[0];
        if (primaryButton?.onPress) {
          setTimeout(() => primaryButton.onPress?.(), 100);
        }
      }
      
      return;
    }
    
    // For alerts with buttons, use window.confirm
    if (buttons && buttons.length > 0) {
      const confirmMessage = message ? `${title}\n\n${message}` : title;
      const result = window.confirm(confirmMessage);
      
      if (result) {
        // User clicked OK - find the primary/destructive button
        const primaryButton = buttons.find(btn => btn.style !== 'cancel') || buttons[buttons.length - 1];
        if (primaryButton?.onPress) {
          primaryButton.onPress();
        }
      } else {
        // User clicked Cancel - find the cancel button
        const cancelButton = buttons.find(btn => btn.style === 'cancel');
        if (cancelButton?.onPress) {
          cancelButton.onPress();
        }
      }
    } else {
      // Simple alert with no buttons
      const alertMessage = message ? `${title}\n\n${message}` : title;
      window.alert(alertMessage);
    }
  },
};
